import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { supabase } from '../../lib/supabase';
import { cn } from '../../lib/cn';
import { useSession } from '../../app/useSession';
import { useUserProfile, userProfileQueryKey } from '../../app/useUserProfile';
import { useToast } from '../../components/Toast';
import styles from './settings.module.css';

const phoneRegex = /^0\d{1,2}-?\d{7}$/;

const profileSchema = z.object({
  full_name: z.string().min(2, 'השם קצר מדי').max(80, 'השם ארוך מדי'),
  phone: z
    .string()
    .optional()
    .refine((v) => !v || phoneRegex.test(v), 'טלפון לא תקין'),
});
type ProfileInput = z.infer<typeof profileSchema>;

interface ProfileRowFull {
  user_id: string;
  full_name: string;
  phone: string | null;
  locale: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

function initialOf(name: string, email: string | null): string {
  const trimmed = name.trim();
  const base = (trimmed.length > 0 ? trimmed : email) ?? '';
  return base.charAt(0).toUpperCase();
}

function formatDate(iso: string): string {
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: he });
  } catch {
    return iso;
  }
}

export function ProfilePage() {
  const session = useSession();
  const userId = session.status === 'signed-in' ? session.session.user.id : null;
  const email = session.status === 'signed-in' ? (session.session.user.email ?? null) : null;
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useUserProfile(userId);
  const [meta, setMeta] = useState<{ phone: string | null; locale: string; timezone: string; created_at: string; updated_at: string } | null>(null);
  const toast = useToast();

  // Hydrate the extra fields not in useUserProfile (phone/timezone/locale/timestamps).
  useEffect(() => {
    if (!userId) return;
    let active = true;
    void supabase
      .from('user_profiles')
      .select('user_id, full_name, phone, locale, timezone, created_at, updated_at')
      .eq('user_id', userId)
      .maybeSingle<ProfileRowFull>()
      .then(({ data }) => {
        if (active && data) {
          setMeta({
            phone: data.phone,
            locale: data.locale,
            timezone: data.timezone,
            created_at: data.created_at,
            updated_at: data.updated_at,
          });
        }
      });
    return () => { active = false; };
  }, [userId, profile?.full_name]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: { full_name: '', phone: '' },
  });

  // Populate form once data arrives.
  useEffect(() => {
    if (profile?.full_name) {
      reset({ full_name: profile.full_name, phone: meta?.phone ?? '' });
    }
  }, [profile?.full_name, meta?.phone, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!userId) return;
    const { error } = await supabase
      .from('user_profiles')
      .update({
        full_name: values.full_name,
        phone: values.phone ?? null,
      })
      .eq('user_id', userId);
    if (error) {
      toast.error('לא הצלחנו לשמור · נסה שוב');
      return;
    }
    toast.success('הפרופיל עודכן');
    await queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
  });

  if (isLoading) return null;

  const fullName = profile?.full_name ?? '';

  return (
    <>
      <div className={styles.pageHead}>
        <h1>פרופיל</h1>
        <p className={styles.pageSub}>הפרטים שלך כפי שהם מופיעים ב-FocusMode. שינויים נכנסים לתוקף מיד.</p>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3>פרטים אישיים</h3>
          <p>השם המלא יוצג לחברי הצוות שלך ובאימיילים החיצוניים.</p>
        </div>

        <div className={styles.avatarRow}>
          <div className={styles.avatar} aria-hidden="true">
            {initialOf(fullName, email)}
          </div>
          <div className={styles.avatarMeta}>
            <span className={styles.avatarName}>{fullName || (email ?? '')}</span>
            <span className={styles.avatarHint}>העלאת תמונה זמינה ב-Phase 1</span>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="profile-name">שם מלא</label>
              <input
                id="profile-name"
                type="text"
                className={cn(styles.input, errors.full_name && styles.invalid)}
                autoComplete="name"
                {...register('full_name')}
              />
              {errors.full_name ? <p className={styles.error}>{errors.full_name.message}</p> : null}
            </div>
            <div className={styles.field}>
              <label htmlFor="profile-email">אימייל</label>
              <input
                id="profile-email"
                type="email"
                className={styles.input}
                value={email ?? ''}
                disabled
                dir="ltr"
              />
              <p className={styles.notice}>שינוי אימייל זמין ב-Phase 1.</p>
            </div>
          </div>

          <div className={styles.row} style={{ marginBlockStart: 'var(--space-3)' }}>
            <div className={styles.field}>
              <label htmlFor="profile-phone">
                טלפון
                <span className={styles.optional}>אופציונלי</span>
              </label>
              <input
                id="profile-phone"
                type="tel"
                inputMode="tel"
                dir="ltr"
                placeholder="054-274-2847"
                className={cn(styles.input, errors.phone && styles.invalid)}
                {...register('phone')}
              />
              {errors.phone ? <p className={styles.error}>{errors.phone.message}</p> : null}
            </div>
            <div className={styles.field}>
              <label htmlFor="profile-tz">אזור זמן</label>
              <input
                id="profile-tz"
                type="text"
                className={styles.input}
                value={meta?.timezone ?? 'Asia/Jerusalem'}
                disabled
                dir="ltr"
              />
              <p className={styles.notice}>עריכת אזור זמן זמינה ב-Phase 1.</p>
            </div>
          </div>

          <div className={styles.actions} style={{ marginBlockStart: 'var(--space-4)' }}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => { reset(); }}
              disabled={isSubmitting || !isDirty}
            >
              בטל
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={isSubmitting}>
              שמור
            </button>
          </div>
        </form>

        {meta ? (
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>נוצר</span>
              <span className={styles.metaValue}>
                <span className="num">{formatDate(meta.created_at)}</span>
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>עדכון אחרון</span>
              <span className={styles.metaValue}>
                <span className="num">{formatDate(meta.updated_at)}</span>
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
